
import React from "react";
import { TooltipText } from "../ui/tooltip";
import { useTranslation } from "react-i18next";
import CountryBadge, { InlineCountryFlag } from "../shipment/CountryBadge";
const { Eye, Printer, Mail, DollarSign, FileText, Truck, Flag, ClipboardList, Cloud, MapPin, Settings, Calendar, StickyNote, Upload, Trash2, CheckCircle, FileBadge, ClipboardListIcon } = require("lucide-react");

const getColumns = (handlers) => {
    const {
        toggleExpandRow,
        onViewShipment,
        navigate,
        setSelectedOrderId,
        setshowNote,
        setFileUploadVisible,
        shippingTypeConfig,
        handleDeleteBooking,
        handleDockReceipt,
        handleAutitStatus,
        _setRestoreDataPopup
    } = handlers;
    const { t } = useTranslation();


    const getLocationName = (location) => {
        if (!location) return "";

        const match = location.match(/\[(.*?)\]/);
        return match ? match[1] : "";
    };
    const formatDate = (dateStr) => {
        if (!dateStr) return "NA";

        try {
            const date = new Date(dateStr);

            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return dateStr;
        }
    };



    return [
        {
            key: 'view',
            label: t('view'),
            render: (shipment) => (
                <button
                    onClick={() => toggleExpandRow(shipment.id)}
                    className="flex justify-center items-center bg-cyan-500 hover:bg-cyan-600 text-white rounded transition-colors w-[28px] h-[28px]"
                    title={t('viewDetails')}
                >
                    <Eye className="w-4 h-4" />
                </button>
            )
        },

        {
            key: 'print',
            label: t('print'),
            render: (shipment) => (
                <a
                    className="flex justify-center items-center bg-cyan-500 hover:bg-cyan-600 text-white rounded transition-colors w-[28px] h-[28px]"
                    href={`/booking/print/${btoa(shipment.id)}`}
                    target="_blank"
                    title={t('print')}
                >
                    <Printer className="w-4 h-4" />
                </a>
            )
        },

        {
            key: 'email',
            label: t('email'),
            render: (shipment) => (
                <button
                   onClick={() => navigate(`/mail/compose?id=${shipment.id}&type=Booking`)}
                    className="flex justify-center items-center hover:bg-slate-100 dark:hover:bg-slate-800 rounded border border-slate-300 dark:border-slate-600 transition-colors w-[28px] h-[28px]"
                    title={t('email')}
                >
                    <Mail className="w-4 h-4 text-cyan-500" />
                </button>
            )
        },

        {
            key: 'bookingNumber',
            label: t('bookingNo'),
            render: (shipment) => (
                <button
                    type="button"
                    onClick={() => onViewShipment(shipment.id)}
                    className="text-sm font-semibold text-[#0072FF]"
                >
                    {shipment?.carrierBookingNumber || t('na')}
                </button>
            )
        },

        {
            key: 'fileNumber',
            label: t('fileRefNo'),
            render: (shipment) => (
                <button
                    type="button"
                    onClick={() => navigate('/hbl', {
                        state: {
                            bookingId: shipment.id,
                            FILENUMBER: shipment.fileNumber
                        }
                    })}
                    className="text-sm font-semibold text-[#0072FF]"
                >
                    {shipment.fileNumber || t('na')}
                </button>
            )
        },

        {
            key: 'ROUTE',
            label: t('route'),
            tdClass: "px-2 py-2 border-b border-slate-200 dark:border-slate-700 min-w-[170px] max-w-[350px]",
            render: (shipment) => {
                const getPort = (name, code) =>
                    getLocationName(name) || code || name;

                const pol = getPort(shipment?.portOfLoading, shipment?.portOfLoadingCode);
                const pod = getPort(shipment?.portOfDischarge, shipment?.portOfDischargeCode);

                const Port = ({ value, code }) => (
                    <span className="flex items-center gap-1 min-w-0" title={value} code={code}>
                        <InlineCountryFlag code={code} />
                        <span className="truncate">{value}</span>
                    </span>
                );

                return (
                    <div className="flex items-center gap-1 text-xs font-medium text-slate-700 dark:text-slate-300 overflow-hidden">
                        <Port value={pol} code={pol} />
                        <span className="shrink-0">→</span>
                        <Port value={pod} code={pod} />
                    </div>
                );
            },
        },

        {
            key: 'ETD',
            label: t('etd'),
            render: (shipment) => (
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {formatDate(shipment?.etsAtPol)}
                </span>
            )
        },

        {
            key: 'ETA',
            label: t('eta'),
            render: (shipment) => (
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {formatDate(shipment?.etaAtPod)}
                </span>
            )
        },

        {
            key: 'customer',
            label: t('customer'),
            render: (shipment) => (
                <TooltipText content={shipment?.customerName}>
                    <span className="text-xs truncate block">
                        {shipment?.customerName || '-'}
                    </span>
                </TooltipText>
            )
        },

        {
            key: 'customerPO',
            label: t('customerPO'),
            render: (shipment) => (
                <span className="text-xs">
                    {shipment.customerReference || t('na')}
                </span>
            )
        },

        {
            key: 'consignee',
            label: t('consignee'),
            render: (shipment) => (
                <TooltipText content={shipment?.consignee}>
                    <span className="text-xs truncate">
                        {shipment?.consignee || '-'}
                    </span>
                </TooltipText>
            )
        },

        {
            key: 'progress',
            label: t('progress'),
            render: () => (
                <Stepper
                    active={0}
                    steps={[
                        t('booked'),
                        t('packed'),
                        t('departed'),
                        t('inTransit'),
                        t('delivered')
                    ]}
                />
            )
        },

        {
            key: 'documents',
            label: t('documents'),
            render: (shipment) => (
                <div className="flex gap-1">

                    <TooltipText content={t('houseBL')}>
                        <button
                            className="w-6 h-6 rounded bg-blue-100 text-blue-700"
                            onClick={() => navigate('/hbl', {
                                state: { bookingId: shipment.id }
                            })}
                        >
                            <FileText className="w-4 h-4" />
                        </button>
                    </TooltipText>

                    <TooltipText content={t('masterBL')}>
                        <button
                            className="w-6 h-6 rounded bg-indigo-100 text-indigo-700"
                            onClick={() => navigate('/mbl', {
                                state: { bookingId: shipment.id }
                            })}
                        >
                            <FileBadge className="w-4 h-4" />
                        </button>
                    </TooltipText>

                    <TooltipText content={t('dockReceipt')}>
                        <button
                            className="w-6 h-6 rounded bg-amber-100 text-amber-700"
                            onClick={() => handleDockReceipt(shipment)}
                        >
                            <ClipboardListIcon className="w-4 h-4" />
                        </button>
                    </TooltipText>

                    <TooltipText content={t('statusAudit')}>
                        <button
                            className="w-6 h-6 rounded bg-purple-100 text-purple-700"
                            onClick={() => handleAutitStatus(shipment)}
                        >
                            <CheckCircle className="w-4 h-4" />
                        </button>
                    </TooltipText>

                </div>
            )
        },

        {
            key: 'carrier',
            label: t('carrier'),
            render: (shipment) => (
                <TooltipText content={shipment?.carrier}>
                    <span className="text-xs truncate">
                        {shipment.carrier || '-'}
                    </span>
                </TooltipText>
            )
        },

        {
            key: 'notes',
            label: t('notes'),
            render: (shipment) => (
                <TooltipText content={t('notes')}>
                    <button
                        onClick={() => {
                            setSelectedOrderId(shipment);
                            setshowNote(true);
                        }}
                        className="w-6 h-6 rounded bg-yellow-100 text-yellow-700"
                    >
                        <StickyNote className="w-4 h-4" />
                    </button>
                </TooltipText>
            )
        },

        {
            key: 'arAp',
            label: t('arAp'),
            render: (shipment) => (
                <div
                    className="flex gap-1"
                    onClick={() => navigate(`/job-costing/${shipment?.id}`)}
                >

                    <TooltipText content={
                        !shipment?.arCharges ?
                            t('invoiceNotCreated') :
                            t('invoiceCreated')
                    }>
                        <span className={`w-5 h-5 text-white flex items-center justify-center text-[12px]
    ${!shipment?.arCharges ?
                                "bg-[#4D9AEF]" :
                                "bg-[#82B958]"
                            }`}>
                            $
                        </span>
                    </TooltipText>

                    <TooltipText content={
                        !shipment?.apCharges ?
                            t('billNotCreated') :
                            t('billCreated')
                    }>
                        <span className={`w-5 h-5 text-white flex items-center justify-center text-[12px]
    ${!shipment?.apCharges ?
                                "bg-[#0098DE]" :
                                "bg-[#82B958]"
                            }`}>
                            P
                        </span>
                    </TooltipText>

                </div>
            )
        },

        {
            key: 'fileManager',
            label: t('fileManager'),
            render: (shipment) => (
                <span className="text-xs">
                    {shipment.operatorName || t('unassigned')}
                </span>
            )
        },

        {
            key: 'upload',
            label: t('upload'),
            render: (shipment) => (
                <div className="flex gap-1">

                    <button
                        onClick={() => {
                            setSelectedOrderId(shipment);
                            setFileUploadVisible(true);
                        }}
                        className="w-6 h-6 rounded bg-emerald-100 text-emerald-700"
                    >
                        <Upload className="w-4 h-4" />
                    </button>

                    <button
                        onClick={() => {
                            setSelectedOrderId(shipment);
                            setFileUploadVisible(true);
                        }}
                        className="w-6 h-6 rounded bg-purple-100 text-purple-700"
                    >
                        <Eye className="w-4 h-4" />
                    </button>

                </div>
            )
        },

        {
            key: 'type',
            label: t('type'),
            render: (shipment) => {
                if (!shipment.shippingType) return null;

                const config = shippingTypeConfig[shipment.shippingType];
                const Icon = config?.icon;

                return (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px]">
                        {Icon && <Icon className="w-3 h-3" />}
                        {shipment.shippingType}
                    </span>
                );
            }
        },

        {
            key: 'vessel',
            label: t('vessel'),
            render: (shipment) => (
                <span className="text-xs">
                    {shipment.vessel || '-'}
                </span>
            )
        },

        {
            key: 'cancel',
            label: t('cancel'),
            render: (shipment) => (
                <button
                    onClick={() => _setRestoreDataPopup(shipment)}
                    className="w-6 h-6 rounded bg-red-100 text-red-700"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            )
        }

    ];
};

export default getColumns;




const Stepper = ({ steps = [], active = 0 }) => {
    return (
        <div className="flex items-center gap-0.5">
            {steps.map((step, index) => {
                const isActive = index === active;
                const isCompleted = index < active;

                return (
                    <div
                        key={index}
                        className="flex items-center relative group animate-fade-in"
                        style={{ animationDelay: `${index * 80}ms` }}
                    >
                        {/* DOT */}
                        <div
                            className={`w-2.5 h-2.5 rounded-full transition-all duration-300
                ${isActive
                                    ? "bg-green-500 animate-dot-pulse ring-2 ring-green-500/30"
                                    : isCompleted
                                        ? "bg-green-500"
                                        : "bg-gray-300 dark:bg-gray-600"
                                }
              `}
                        />

                        {/* TOOLTIP */}
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2
              hidden group-hover:block z-50">
                            <div className="px-2 py-1 text-xs rounded-md shadow-md
                bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700">
                                {step}
                            </div>
                        </div>

                        {/* LINE */}
                        {index !== steps.length - 1 && (
                            <div
                                className={`w-2 h-0.5 transition-all duration-500
                  ${index < active
                                        ? "bg-green-500"
                                        : "bg-gray-300 dark:bg-gray-600"
                                    }
                `}
                                style={{ transitionDelay: `${index * 100}ms` }}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};


